/**
 * Reusable chat panel component.
 * Extracted from ProjectGenerate inline chat for reuse across project and workspace scopes.
 * Supports persistent chat sessions and clickable source badges.
 */

import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { UseMutationResult } from "@tanstack/react-query";
import type { ChatMessage, ProjectChatResponse } from "../services/naraDoor";
import { useChatSession } from "../hooks/useChatSessions";
import { MessageSquare, Send, Loader2 } from "lucide-react";

type ChatMutation = UseMutationResult<
  ProjectChatResponse,
  Error,
  { message: string; history: ChatMessage[]; sessionId?: string; contextSessionIds?: string[] }
>;

interface ChatPanelProps {
  mutation: ChatMutation;
  title: string;
  description: string;
  emptyMessage: string;
  height?: string;
  showSourceAttribution?: boolean;
  sessionId: string | null;
  onSessionIdChange: (id: string) => void;
  contextSessionIds?: string[];
  onSourceClick?: (source: { document_id: string; project_id?: string }) => void;
}

export function ChatPanel({
  mutation,
  title,
  description,
  emptyMessage,
  height = "h-[calc(100vh-200px)]",
  showSourceAttribution = false,
  sessionId,
  onSessionIdChange,
  contextSessionIds,
  onSourceClick,
}: ChatPanelProps) {
  const { t } = useTranslation();
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [lastSources, setLastSources] = useState<
    Map<number, ProjectChatResponse["sources"]>
  >(new Map());

  // Load existing session messages
  const { data: sessionData, isError: sessionError } = useChatSession(sessionId);

  // A refetch that lands while a question is out holds a session without it; showing that
  // would drop the question. The sent exchange stays until the next refetch brings it back.
  const sendingRef = useRef(false);

  useEffect(() => {
    if (sendingRef.current) return;
    if (sessionData && sessionData.messages) {
      const messages: ChatMessage[] = sessionData.messages.map((msg) => ({
        role: msg.role as "user" | "assistant",
        content: msg.content,
      }));
      setChatMessages(messages);

      // Rebuild sources map from session messages
      const sourcesMap = new Map<number, ProjectChatResponse["sources"]>();
      sessionData.messages.forEach((msg, i) => {
        if (msg.role === "assistant" && msg.sources && msg.sources.length > 0) {
          sourcesMap.set(i, msg.sources);
        }
      });
      setLastSources(sourcesMap);
    }
  }, [sessionData]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const handleSendMessage = async () => {
    const message = chatInput.trim();
    if (!message || mutation.isPending) return;

    const userMessage: ChatMessage = { role: "user", content: message };
    const updatedHistory = [...chatMessages, userMessage];
    setChatMessages(updatedHistory);
    setChatInput("");
    sendingRef.current = true;

    try {
      const result = await mutation.mutateAsync({
        message,
        history: chatMessages,
        sessionId: sessionId ?? undefined,
        contextSessionIds,
      });
      const assistantIndex = updatedHistory.length;
      setChatMessages([
        ...updatedHistory,
        { role: "assistant", content: result.response },
      ]);
      if (result.sources && result.sources.length > 0) {
        setLastSources((prev) => {
          const next = new Map(prev);
          next.set(assistantIndex, result.sources);
          return next;
        });
      }
      // Track the session after first response
      if (result.session_id && result.session_id !== sessionId) {
        onSessionIdChange(result.session_id);
      }
    } catch {
      setChatMessages([
        ...updatedHistory,
        { role: "assistant", content: t("projectGenerate.chatError") },
      ]);
    } finally {
      sendingRef.current = false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div
      className={`bg-card rounded-lg border border-border flex flex-col ${height} min-h-[400px]`}
    >
      {/* Chat Header */}
      <div className="px-4 py-3 border-b border-border">
        <h3 className="font-medium text-foreground flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          {title}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {sessionError && <p className="text-sm text-destructive">{t("chatSidebar.sessionLoadError")}</p>}
        {chatMessages.length === 0 && (
          <div className="text-center text-muted-foreground text-sm py-8">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>{emptyMessage}</p>
          </div>
        )}
        {chatMessages.map((msg, i) => (
          <div key={i}>
            <div
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === "user"
                    ? "bg-blue-600 text-white"
                    : "bg-muted text-foreground"
                }`}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
            {/* Source badges for assistant messages */}
            {msg.role === "assistant" && lastSources.get(i) && (
              <div className="flex flex-wrap gap-1.5 mt-1.5 ml-1">
                {lastSources.get(i)!.map((src, j) => (
                  <button
                    key={j}
                    onClick={() => {
                      if (onSourceClick) {
                        onSourceClick(src);
                      }
                    }}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900 cursor-pointer transition-colors"
                  >
                    {showSourceAttribution && src.project_name
                      ? `${src.project_name} > ${src.document_name}`
                      : src.document_name}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        {mutation.isPending && (
          <div className="flex justify-start">
            <div className="bg-muted rounded-lg px-3 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Chat Input */}
      <div className="px-4 py-3 border-t border-border">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("projectGenerate.chatPlaceholder")}
            disabled={mutation.isPending}
            className="flex-1 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 bg-background text-foreground"
          />
          <button
            onClick={handleSendMessage}
            disabled={!chatInput.trim() || mutation.isPending}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
